import { ampli } from '@/ampli';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useRouter } from 'next/router';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { useMedia } from 'react-use';

import { ClientInputError } from '@/lib/errors/client';

import type { TradeDetails } from '@/components/Modal/TradeDetailsModal';
import {
  getOpenOrderColumns,
  OPEN_ORDER_COLUMN_CLASS,
} from '@/components/TradeHistoryTable/columns/openOrderColumns';
import { TableColumn } from '@/components/TradeHistoryTable/config';
import { DataTable } from '@/components/TradeHistoryTable/DataTable';
import { DegradedProvidersWarning } from '@/components/TradeHistoryTable/DegradedProvidersWarning';
import { TableSkeleton } from '@/components/TradeHistoryTable/TableSkeleton';
import { getTradeDetails } from '@/components/TradeHistoryTable/utils';

import { useLastPage, usePagination } from '@/hooks/usePagination';
import { type LimitOrderFunders, useWalletOpenLimitOrders } from '@/hooks/useWalletOpenLimitOrders';

import { breakpoints } from '@/styles/util';

import { Pagination } from '@/ui/Pagination';

import type { LimitOrderItem } from '@/utils/0x.types';
import type { TokenResult } from '@/utils/0x/token-registry.types';
import { trackError } from '@/utils/errors';
import { getLimitOrderIdentityKey } from '@/utils/limit-order-identity';
import { tokenKey } from '@/utils/normalizeAddress';
import { applyOpenLimitOrderTombstones } from '@/utils/open-limit-order-tombstones';
import { routes } from '@/utils/routes';

const PAGE_SIZE = 12;
// Matches the orderbook API cap. Used to fetch every order in one page when
// pair filtering, so the filter applies across all of the maker's orders.
const MAX_PER_PAGE = 1000;

type PairFilter = {
  sellToken: TokenResult | undefined;
  buyToken: TokenResult | undefined;
};

const SKELETON_COLUMNS: TableColumn[] = [
  { key: 'type', label: 'Type' },
  { key: 'sellAmount', label: 'Sell' },
  { key: 'buyAmount', label: 'Buy' },
  { key: 'price', label: 'Price' },
  { key: 'expiry', label: 'Expiration' },
  { key: 'network', label: 'Network' },
  { key: 'status', label: 'Status' },
  { key: 'action', label: 'Actions' },
];

type LimitOpenOrdersTableProps = {
  funders: LimitOrderFunders;
  /** Opens the trade details modal for the clicked order. */
  setSelectedTradeDetails: Dispatch<SetStateAction<TradeDetails | undefined>>;
  /**
   * When set, only orders whose two tokens match this pair (either direction,
   * same chain) are shown. Omit to show every open order.
   */
  pairFilter?: PairFilter;
};

/**
 * Open limit orders table for the limit order page, built on the shared
 * `DataTable` + open-order column factory. Shows the maker's open orders across
 * all chains (the Network column disambiguates) and reads directly from the
 * `limit-orders` query cache, with tombstones hiding freshly cancelled orders.
 */
export function LimitOpenOrdersTable({
  funders,
  setSelectedTradeDetails,
  pairFilter,
}: LimitOpenOrdersTableProps) {
  const router = useRouter();
  const isDesktop = useMedia(breakpoints.desktop);

  const { sellToken, buyToken } = pairFilter ?? {};
  const isPairFilterActive = Boolean(pairFilter && sellToken && buyToken);

  const paginationProps = usePagination({ pageSize: PAGE_SIZE });

  // When filtering by pair we fetch all orders in one page and filter/collapse
  // pagination client-side, since the matching orders may span server pages.
  const { data, isLoading, isRefetching, refetch } = useWalletOpenLimitOrders({
    funders,
    page: isPairFilterActive ? 1 : paginationProps.currentPage,
    limit: isPairFilterActive ? MAX_PER_PAGE : PAGE_SIZE,
  });

  const { records, total } = useMemo(
    () => (data ? applyOpenLimitOrderTombstones(data) : { records: [], total: 0 }),
    [data],
  );

  const filteredRecords = useMemo(() => {
    if (!isPairFilterActive || !sellToken || !buyToken) return records;

    const pairKeys = new Set([
      tokenKey(sellToken.address, sellToken.chainId),
      tokenKey(buyToken.address, buyToken.chainId),
    ]);

    return records.filter(
      (order) =>
        pairKeys.has(tokenKey(order.makerToken, order.chainId)) &&
        pairKeys.has(tokenKey(order.takerToken, order.chainId)),
    );
  }, [records, isPairFilterActive, sellToken, buyToken]);

  const { lastPage } = useLastPage({
    totalItems: isPairFilterActive ? filteredRecords.length : total,
    itemsPerPage: isPairFilterActive ? MAX_PER_PAGE : PAGE_SIZE,
  });

  const columns = useMemo(() => getOpenOrderColumns({ showPrice: true }), []);
  const table = useReactTable({
    data: filteredRecords,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: getLimitOrderIdentityKey,
  });

  const handleRowClick = (item: LimitOrderItem) => {
    if (!item.makerTokenResult) {
      trackError(new ClientInputError('Maker token info not found in open orders'), {
        makerToken: item.makerToken,
      });
      return;
    }
    if (!item.takerTokenResult) {
      trackError(new ClientInputError('Taker token info not found in open orders'), {
        takerToken: item.takerToken,
      });
      return;
    }

    if (isDesktop) {
      router.push({
        pathname: routes.SWAP,
        query: {
          mode: 'advanced',
          ...(item.advancedOrderType && item.advancedOrderType !== 'limit'
            ? { orderType: item.advancedOrderType }
            : {}),
          sellAddress: item.makerToken,
          sellChain: item.makerTokenResult.chainId.toString(),
          buyAddress: item.takerToken,
          buyChain: item.takerTokenResult.chainId.toString(),
        },
      });
    } else {
      ampli.historyDetailViewed();
      setSelectedTradeDetails(getTradeDetails(item));
    }
  };

  if (isLoading) {
    return <TableSkeleton numRows={PAGE_SIZE} columns={SKELETON_COLUMNS} />;
  }

  const degradedProvidersWarning = (
    <DegradedProvidersWarning
      className="m-4"
      degradedProviders={data?.degradedProviders ?? []}
      isRetrying={isRefetching}
      onRetry={() => void refetch()}
    />
  );

  if (filteredRecords.length === 0) {
    return (
      <>
        {degradedProvidersWarning}
        <div className="text-center py-12">
          {isPairFilterActive ? 'No open orders for this pair.' : 'No open orders.'}
        </div>
      </>
    );
  }

  return (
    <>
      {degradedProvidersWarning}
      <DataTable table={table} columnClass={OPEN_ORDER_COLUMN_CLASS} onRowClick={handleRowClick} />

      {lastPage > 1 && (
        <div className="border-t-1 border-special-border-divider w-full py-4 pl-7">
          <Pagination {...paginationProps} lastPage={lastPage} />
        </div>
      )}
    </>
  );
}
